'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { Leaf, MapPin, Mail, Phone, Linkedin, ExternalLink } from 'lucide-react';
import Logo from './Logo';

function Footer() {
  const t = useTranslations('Footer');
  const currentYear = 2025;

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main footer content */}
      <div className="custom-container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-woodland-700 flex items-center justify-center">
                <Leaf className="w-5 h-5 text-woodland-200" />
              </div>
              <span className="text-xl font-semibold">Future Proof Commodities</span>
            </div>
            <p className="text-gray-400 leading-relaxed max-w-md">
              Transforming EUDR compliance from a burden into your competitive advantage.
              We help commodity businesses turn traceability data into powerful sustainability
              narratives that build trust and create value.
            </p>
            <div className="mt-6 flex items-center gap-4">
              <a
                href="https://www.linkedin.com/company/future-proof-commodities"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-gray-800 hover:bg-woodland-700 flex items-center justify-center transition-colors group"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
              Navigation
            </h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="#why-matters"
                  className="text-gray-300 hover:text-white transition-colors inline-flex items-center gap-1"
                >
                  Why It Matters
                </a>
              </li>
              <li>
                <a
                  href="#what-sets-us-apart"
                  className="text-gray-300 hover:text-white transition-colors inline-flex items-center gap-1"
                >
                  What Sets Us Apart
                </a>
              </li>
              <li>
                <a
                  href="#the-story"
                  className="text-gray-300 hover:text-white transition-colors inline-flex items-center gap-1"
                >
                  The Story
                </a>
              </li>
              <li>
                <a
                  href="#contact"
                  className="text-gray-300 hover:text-white transition-colors inline-flex items-center gap-1"
                >
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
              Contact
            </h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="mailto:rene@futureproofcommodities.com"
                  className="text-gray-300 hover:text-white transition-colors inline-flex items-center gap-2"
                >
                  <Mail className="w-4 h-4 text-woodland-500" />
                  <span className="text-sm">rene@futureproofcommodities.com</span>
                </a>
              </li>
              <li>
                <a
                  href="tel:+31651979052"
                  className="text-gray-300 hover:text-white transition-colors inline-flex items-center gap-2"
                >
                  <Phone className="w-4 h-4 text-woodland-500" />
                  <span className="text-sm">+31 6 51979052</span>
                </a>
              </li>
              <li className="text-gray-400 inline-flex items-start gap-2">
                <MapPin className="w-4 h-4 text-woodland-500 mt-0.5 shrink-0" />
                <span className="text-sm">The Netherlands</span>
              </li>
            </ul>

            {/* Trust badges */}
            <div className="mt-6 pt-6 border-t border-gray-800">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Partners</p>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-gray-400 bg-gray-800 px-3 py-1.5 rounded-full">
                  Wageningen UR
                </span>
                <span className="text-xs text-gray-400 bg-gray-800 px-3 py-1.5 rounded-full">
                  Eurofins
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-800">
        <div className="custom-container py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">
              &copy; {currentYear} Future Proof Commodities B.V. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-sm text-gray-500 hover:text-gray-300 transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-sm text-gray-500 hover:text-gray-300 transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
